import React, { createContext, useContext, useMemo, useState } from 'react';
import { captureException } from 'utils/exception';

import { Codeowners } from 'components/Common/Error/Codeowners';

import { MyMoneyNotificationsCountMethodsContext, MyMoneyNotificationsCountValueContext } from './types';

const defaultNoop = () => {
  throw Error('myMoneyNotificationsCountContext was called outside of the provider');
};

const valueContext = createContext<MyMoneyNotificationsCountValueContext>({
  openDisputesCount: 0,
  consumerFinancingNotificationsCount: 0,
  accountingNotificationsCount: 0,
});

const methodContext = createContext<MyMoneyNotificationsCountMethodsContext>({
  decrementPieceOfState: defaultNoop,
  setPieceOfState: defaultNoop,
});

type Props = MyMoneyNotificationsCountValueContext & { children: React.ReactNode };

export const MyMoneyNotificationsCountProvider = ({ children, ...initialValues }: Props) => {
  const [state, setState] = useState(initialValues);
  const methods = useMemo<MyMoneyNotificationsCountMethodsContext>(
    () => ({
      decrementPieceOfState: (pieceOfState) => {
        setState((prev) => {
          const previousValue = prev[pieceOfState];

          if (typeof previousValue !== 'number') {
            captureException(
              `Non-number property in MyMoneyNotificationsCount context. Property name: ${pieceOfState} with value: ${previousValue}`,
              Codeowners.FINTECH_B2B_FINANCING
            );
            return prev;
          }

          if (previousValue > 0) {
            return { ...prev, [pieceOfState]: previousValue - 1 };
          }
          return prev;
        });
      },
      setPieceOfState: (pieceOfState, value) => {
        setState((prev) => {
          return { ...prev, [pieceOfState]: value };
        });
      },
    }),
    [setState]
  );

  return (
    <valueContext.Provider value={state}>
      <methodContext.Provider value={methods}>{children}</methodContext.Provider>
    </valueContext.Provider>
  );
};

export const useMyMoneyNotificationsCount = () => {
  const values = useContext(valueContext);

  return (
    Object.entries(values).reduce((total, [pieceOfState, current]) => {
      if (typeof current !== 'number') {
        captureException(
          `Non-number property in MyMoneyNotificationsCount context. Property name: ${pieceOfState} with value: ${current}`,
          Codeowners.FINTECH_B2B_FINANCING
        );
        return total;
      }
      return total + current;
    }, 0) || 0
  );
};

export const useOpenDisputesCount = () => useContext(valueContext).openDisputesCount;
export const useDecrementOpenDisputesCount = () => {
  const { decrementPieceOfState } = useContext(methodContext);

  return () => decrementPieceOfState('openDisputesCount');
};

export const useConsumerFinancingNotificationsCount = () =>
  useContext(valueContext).consumerFinancingNotificationsCount;
export const useDecrementConsumerFinancingNotificationsCount = () => {
  const { decrementPieceOfState } = useContext(methodContext);

  return () => decrementPieceOfState('consumerFinancingNotificationsCount');
};
export const useSetAccountingMyMoneyNotificationsCount = () => {
  const { setPieceOfState } = useContext(methodContext);

  return (value: number) => setPieceOfState('accountingNotificationsCount', value);
};
